// CurrentUserProvider — fetches /auth/me and provides role/permission context.
// Wrap inside <AuthGuard> so it only fires when the user is authenticated.

import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useIsAuthenticated } from '@azure/msal-react'
import { apiFetch } from '../../lib/api'
import { DEV_AUTH_BYPASS } from '../../lib/devAuth'
import { CurrentUserContext } from '../../hooks/useCurrentUser'
import type { CurrentUser, PermissionPage } from '@portico/types'

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated()

  const { data, isLoading } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => apiFetch<{ data: CurrentUser }>('/api/v1/auth/me'),
    // No MSAL account in local dev sign-in (ADR-017); the API decides who we are.
    enabled: isAuthenticated || DEV_AUTH_BYPASS,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 2,
    refetchOnWindowFocus: false,
  })

  const user = data?.data ?? null

  const canView = (page: PermissionPage): boolean => {
    // While loading, allow all — prevents flash of empty sidebar
    if (isLoading) return true
    if (!user) return false
    const perm = user.permissions.find((p) => p.page === page)
    return perm?.canView ?? false
  }

  const canEdit = (page: PermissionPage): boolean => {
    if (isLoading) return true
    if (!user) return false
    const perm = user.permissions.find((p) => p.page === page)
    return perm?.canEdit ?? false
  }

  const hasRole = (role: string): boolean => {
    if (!user) return false
    return user.roles.includes(role as CurrentUser['roles'][number])
  }

  return (
    <CurrentUserContext.Provider value={{ user, isLoading, canView, canEdit, hasRole }}>
      {children}
    </CurrentUserContext.Provider>
  )
}
